import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { WIZARD } from "@/constants/testIds";
import { fmtDateTime } from "@/lib/format";
import { TenderAnalysisPanel } from "@/components/TenderAnalysisPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Rocket, Trophy, FileSearch, ArrowLeft, ArrowUpRight, Lock, CheckCircle2,
  Circle, RotateCcw, Sparkles, Clock,
} from "lucide-react";
import { toast } from "sonner";

export default function WizardPage() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get("id");
  const { hasRole } = useAuth();
  const canDrive = hasRole("tenant_admin", "contract_manager");

  if (id) {
    return <WizardView id={id} canDrive={canDrive} onBack={() => setSp({})} />;
  }
  return <Launcher canDrive={canDrive} onOpen={(wid) => setSp({ id: wid })} />;
}

/* ------------------------------ Launcher ------------------------------ */
function Launcher({ canDrive, onOpen }) {
  const ws = useWorkspace();
  const navigate = useNavigate();
  const [track, setTrack] = useState("won");
  const [contractId, setContractId] = useState(ws.contractId || "new");
  const [list, setList] = useState([]);
  const [starting, setStarting] = useState(false);

  const loadList = useCallback(() => {
    api.get("/wizard").then((r) => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const start = async () => {
    setStarting(true);
    try {
      const body = track === "won"
        ? { track: "won", contract_id: contractId === "new" ? null : contractId }
        : { track: "tender" };
      const { data } = await api.post("/wizard", body);
      onOpen(data.id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={WIZARD.launcher}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/setup")} className="text-forest-mid">
          <ArrowLeft className="mr-1 h-4 w-4" /> Setup
        </Button>
      </div>
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark">
          <Rocket className="h-6 w-6 text-forest-mid" /> Start a project
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          A guided checklist that walks you through standing up a project end-to-end — from legal entity
          to an issued score and a tender or report. Pick where you're starting from.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <TrackCard
          testid={WIZARD.trackWon}
          active={track === "won"}
          onSelect={() => setTrack("won")}
          icon={Trophy}
          recommended
          title="Won / Awarded project"
          desc="The full ROOTS journey — operations, evidence and scoring on a real contract. The evidence you capture here also feeds future tenders."
        >
          {track === "won" && (
            <div className="mt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
              <label className="text-xs font-semibold text-forest-dark">Attach to a contract</label>
              <Select value={contractId} onValueChange={setContractId}>
                <SelectTrigger data-testid={WIZARD.contractSelect} className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Start a new project (no contract yet)</SelectItem>
                  {ws.contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">New projects build the contract during the wizard.</p>
            </div>
          )}
        </TrackCard>

        <TrackCard
          testid={WIZARD.trackTender}
          active={track === "tender"}
          onSelect={() => setTrack("tender")}
          icon={FileSearch}
          title="Tender (pursuit)"
          desc="A lighter path to win new work — reuse verified evidence from existing projects to assemble a Tender Pack. No operational data of its own."
        />
      </div>

      {canDrive ? (
        <Button onClick={start} disabled={starting} className="bg-forest-mid hover:bg-forest-dark" data-testid={WIZARD.startBtn}>
          <Sparkles className="mr-1.5 h-4 w-4" /> {starting ? "Starting…" : `Start ${track === "won" ? "won project" : "tender"} wizard`}
        </Button>
      ) : (
        <p className="rounded-lg border border-sage-light bg-sage-surface p-3 text-sm text-muted-foreground">
          Only tenant admins and contract managers can start a project. You can open a project below to view its progress.
        </p>
      )}

      {list.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Resume a project</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {list.map((w) => (
              <button key={w.id} onClick={() => onOpen(w.id)} data-testid={WIZARD.resumeRow(w.id)}
                className="group flex items-center justify-between gap-3 rounded-xl border border-sage-light bg-white p-4 text-left transition-colors hover:border-forest-mid">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {w.track === "won" ? <Trophy className="h-4 w-4 text-forest-mid" /> : <FileSearch className="h-4 w-4 text-forest-mid" />}
                    <p className="truncate font-heading text-sm font-semibold text-forest-dark">{w.title}</p>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> Updated {fmtDateTime(w.updated_at)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="font-mono text-sm font-bold text-forest-mid">{w.overall_percent}%</span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-forest-mid" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrackCard({ testid, active, onSelect, icon: Icon, title, desc, recommended, children }) {
  return (
    <div role="button" tabIndex={0} onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      data-testid={testid}
      className={cn(
        "flex cursor-pointer flex-col rounded-2xl border bg-white p-5 text-left transition-all",
        active ? "border-forest-mid ring-2 ring-forest-mid/20" : "border-sage-light hover:border-sage-accent"
      )}>
      <div className="flex items-center justify-between">
        <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl", active ? "bg-forest-mid text-white" : "bg-sage-surface text-forest-mid")}>
          <Icon className="h-5 w-5" />
        </div>
        {recommended && <span className="rounded-full bg-sage-accent/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-forest-dark">Recommended</span>}
      </div>
      <h3 className="mt-3 font-heading text-lg font-bold text-forest-dark">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{desc}</p>
      {children}
    </div>
  );
}

/* ------------------------------ Wizard view ------------------------------ */
function WizardView({ id, canDrive, onBack }) {
  const navigate = useNavigate();
  const [wiz, setWiz] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wizard/${id}`);
      setWiz(data);
      setActive((prev) => {
        if (prev != null) return prev;
        const firstOpen = data.phases.findIndex((p) => !p.locked && !p.complete);
        return firstOpen >= 0 ? firstOpen : 0;
      });
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const tick = async (item, done) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wizard/${id}/items/${item.key}`, { done });
      setWiz(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wizard/${id}/reset`);
      setWiz(data);
      toast.success("Manual progress cleared");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!wiz) return <p className="text-sm text-muted-foreground">Loading project…</p>;
  const phase = wiz.phases[active] || wiz.phases[0];

  return (
    <div className="space-y-5" data-testid={WIZARD.view}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="mb-1 text-forest-mid" data-testid={WIZARD.backBtn}>
            <ArrowLeft className="mr-1 h-4 w-4" /> All projects
          </Button>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark">
            {wiz.track === "won" ? <Trophy className="h-6 w-6 text-forest-mid" /> : <FileSearch className="h-6 w-6 text-forest-mid" />}
            {wiz.title}
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {wiz.track === "won" ? "Won project" : "Tender pursuit"} · started by {wiz.created_by_name || "—"}
          </p>
        </div>
        {canDrive && (
          <Button variant="outline" size="sm" onClick={reset} disabled={busy} data-testid={WIZARD.resetBtn}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset progress
          </Button>
        )}
      </div>

      {/* overall progress */}
      <div className="rounded-xl border border-sage-light bg-white p-4" data-testid={WIZARD.overall}>
        <div className="mb-1.5 flex items-center justify-between text-sm">
          <span className="font-semibold text-forest-dark">Overall progress</span>
          <span className="font-mono font-bold text-forest-mid">{wiz.overall_percent}%</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-sage-surface">
          <div className="h-full rounded-full bg-forest-mid transition-all" style={{ width: `${wiz.overall_percent}%` }} />
        </div>
        {wiz.track === "won" && !wiz.gate_passed && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-amber-700">
            <Lock className="h-3 w-3" /> Complete Organisation, Places &amp; assets and Contract to unlock the operational phases.
          </p>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[260px,1fr]">
        {/* phase rail */}
        <div className="space-y-1.5">
          {wiz.phases.map((p, idx) => {
            const state = p.locked ? "locked" : p.complete ? "done" : "active";
            return (
              <button key={p.key} onClick={() => setActive(idx)} data-testid={WIZARD.phase(p.key)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                  idx === active ? "border-forest-mid bg-sage-surface" : "border-transparent hover:bg-sage-surface/60",
                  p.locked && "opacity-55"
                )}>
                <span className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                  state === "done" ? "bg-forest-mid text-white" : state === "locked" ? "bg-slate-200 text-slate-500" : "border-2 border-forest-mid text-forest-mid"
                )}>
                  {state === "done" ? <CheckCircle2 className="h-4 w-4" /> : state === "locked" ? <Lock className="h-3 w-3" /> : idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-forest-dark">{p.title}</p>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-sage-light">
                    <div className="h-full bg-sage-accent" style={{ width: `${p.percent}%` }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* checklist + tender analysis */}
        <div className="space-y-5">
          <Card className="border-sage-light p-5">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="font-heading text-lg font-bold text-forest-dark">{phase.title}</h2>
            {phase.gated && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">Gated</span>}
            {phase.complete && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Complete</span>}
          </div>
          {phase.locked && (
            <p className="mb-3 flex items-center gap-1.5 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
              <Lock className="h-3.5 w-3.5" /> {phase.lock_hint || "Complete the previous phase first."}
            </p>
          )}

          <div className="space-y-2.5">
            {phase.items.map((it) => (
              <div key={it.key} data-testid={WIZARD.item(it.key)}
                className={cn("flex items-start gap-3 rounded-xl border p-3", it.done ? "border-emerald-200 bg-emerald-50/40" : "border-sage-light bg-white")}>
                <div className="pt-0.5">
                  {it.done ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : it.manual_allowed && canDrive && !phase.locked ? (
                    <Checkbox checked={it.source === "manual"} onCheckedChange={(ck) => tick(it, !!ck)} disabled={busy} data-testid={WIZARD.itemTick(it.key)} aria-label="Mark done" />
                  ) : (
                    <Circle className="h-5 w-5 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-forest-dark">{it.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{it.hint}</p>
                  {it.done && (
                    <span className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      {it.source === "auto" ? "Detected from your data" : "Marked done"}
                    </span>
                  )}
                </div>
                {!phase.locked && (
                  <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => {
                    if (it.jump?.startsWith("#")) {
                      document.getElementById(it.jump.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
                    } else {
                      navigate(it.jump);
                    }
                  }} data-testid={WIZARD.itemJump(it.key)}>
                    Open <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          </Card>
          {wiz.track === "tender" && phase.key === "pursuit" && (
            <div id="analyze">
              <TenderAnalysisPanel wizardId={id} analysis={wiz.tender_analysis} canDrive={canDrive} onChanged={load} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
